import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme, usePageTitle, AlertBanner } from '@flowcore-water/ui';
import { fetchRecord, type RecordDetail as RecordDetailType } from '../lib/api';
import { RawJsonViewer } from '../components/RawJsonViewer';
import { AssetRenderer, isAssetField } from '../components/AssetRenderer';

const HIDDEN_FIELDS = new Set(['raw', 'synced_at']);

export function RecordDetail() {
  const { entity, id } = useParams<{ entity: string; id: string }>();
  const [data, setData] = useState<RecordDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTheme();
  usePageTitle('ST Mirror', `${entity ?? 'Record'} #${id ?? ''}`);

  useEffect(() => {
    if (!entity || !id) return;
    setLoading(true);
    fetchRecord(entity, Number(id))
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [entity, id]);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <span style={{ color: t.textMuted }}>Loading...</span>
      </div>
    );
  }

  if (error) {
    return <AlertBanner variant="error" message={error} />;
  }

  if (!data) return null;

  const record = data.record;
  const fields = Object.entries(record).filter(([k]) => !HIDDEN_FIELDS.has(k));

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-sm" style={{ color: t.textMuted }}>
        <Link to="/" style={{ color: t.accent }}>Entities</Link>
        <span>/</span>
        <Link to={`/browse/${entity}`} style={{ color: t.accent }}>
          {entity}
        </Link>
        <span>/</span>
        <span style={{ color: t.textPrimary }}>{id}</span>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Fields */}
        <div className="lg:col-span-2">
          <div
            className="rounded-xl border p-5"
            style={{ background: t.cardBg, borderColor: t.borderSubtle }}
          >
            <h1 className="mb-4 text-xl font-bold" style={{ color: t.textPrimary }}>
              {String(record.name || record.summary || record.invoice_number || record.appointment_number || record.technician_name || `#${id}`)}
            </h1>

            <div className="grid gap-3 sm:grid-cols-2">
              {fields.map(([key, value]) =>
                isAssetField(key, value) ? (
                  <div key={key} className="sm:col-span-2">
                    <div
                      className="text-xs font-mono uppercase tracking-wide mb-1"
                      style={{ color: t.textMuted }}
                    >
                      {key.replace(/_/g, ' ')}
                    </div>
                    <AssetRenderer
                      entity={entity!}
                      recordId={Number(id)}
                      field={key}
                      value={String(value)}
                    />
                  </div>
                ) : (
                  <FieldRow key={key} label={key} value={value} />
                ),
              )}
            </div>

            {record['raw'] != null && (
              <div className="mt-5 border-t pt-4" style={{ borderColor: t.borderSubtle }}>
                <RawJsonViewer data={record['raw'] as string} label="Raw API Response" />
              </div>
            )}
          </div>
        </div>

        {/* Related entities sidebar */}
        <div>
          <div
            className="rounded-xl border p-5"
            style={{ background: t.cardBg, borderColor: t.borderSubtle }}
          >
            <h2
              className="mb-3 text-xs font-mono uppercase tracking-wide"
              style={{ color: t.sectionHeading }}
            >
              Related Entities
            </h2>
            {data.joins.length === 0 ? (
              <p className="text-sm" style={{ color: t.textMuted }}>No related entities</p>
            ) : (
              <div className="space-y-2">
                {data.joins.map((join) => (
                  <Link
                    key={join.key}
                    to={`/join/${join.key}?id=${id}`}
                    className="flex items-center justify-between rounded-lg border p-3 transition-colors"
                    style={{ borderColor: t.borderSubtle }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = t.surfaceHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span className="text-sm" style={{ color: t.textSecondary }}>
                      {join.display}
                    </span>
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-mono"
                      style={{ background: t.accentBg, color: t.accent }}
                    >
                      {join.count}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: unknown }) {
  const { t } = useTheme();
  const display: string = formatValue(value);

  return (
    <div className="overflow-hidden">
      <div
        className="text-xs font-mono uppercase tracking-wide"
        style={{ color: t.textMuted }}
      >
        {label.replace(/_/g, ' ')}
      </div>
      <div
        className="mt-0.5 text-sm break-all"
        style={{ color: t.textPrimary }}
      >
        {display}
      </div>
    </div>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}
